// Copyright 2015 Grafana Labs
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

// This file uses source code from
// https://github.com/grafana/grafana/blob/v10.4.4/packages/grafana-data/src/transformations/transformers/merge.ts

import { omit } from 'lodash';
import { DataFrame, Field, FieldType } from '@/core';
import { MutableDataFrame } from '@/core/data-frame/MutableDataFrame';

interface ValuePointer {
  key: string;
  index: number;
}

/**
 * This combines the results from multiple queries into a single result,
 * which is particularly useful when using the table panel visualization.
 * This transformation merges values into the same row if the shared fields contain the same data.
 */
export const merge = (dataFrames: DataFrame[]): DataFrame[] => {
  if (!Array.isArray(dataFrames) || dataFrames.length <= 1) {
    return dataFrames;
  }

  const data = dataFrames.filter((frame) => frame.fields.length > 0);

  const firstDataFrame = dataFrames[0];
  if (data.length === 0 && firstDataFrame) {
    return [firstDataFrame];
  }

  const fieldNames = new Set<string>();
  const fieldIndexByName: Record<string, Record<number, number>> = {};
  const fieldNamesForKey: string[] = [];
  const dataFrame = new MutableDataFrame();

  for (let frameIndex = 0; frameIndex < data.length; frameIndex++) {
    const frame = data[frameIndex];

    if (!frame) continue; // skip if frame is undefined

    for (let fieldIndex = 0; fieldIndex < frame.fields.length; fieldIndex++) {
      const field = frame.fields[fieldIndex];
      if (!field) continue; // skip if field is undefined

      if (fieldNames.has(field.name) && field.type === FieldType.number) {
        field.name = `${field.name} (${frameIndex + 1})`;
      }
      if (!fieldNames.has(field.name)) {
        dataFrame.addField(copyFieldStructure(field));
        fieldNames.add(field.name);
      }

      fieldIndexByName[field.name] = fieldIndexByName[field.name] || {};
      const fieldIndexMap = fieldIndexByName[field.name];
      if (fieldIndexMap) {
        fieldIndexMap[frameIndex] = fieldIndex;
      }

      if (data.length - 1 !== frameIndex) {
        continue;
      }

      if (fieldExistsInAllFrames(fieldIndexByName, field, data)) {
        fieldNamesForKey.push(field.name);
      }
    }
  }

  if (fieldNamesForKey.length === 0) {
    return dataFrames;
  }

  const valuesByKey: Record<string, Array<Record<string, unknown>>> = {};
  const valuesInOrder: ValuePointer[] = [];
  const keyFactory = createKeyFactory(data, fieldIndexByName, fieldNamesForKey);
  const valueMapper = createValueMapper(data, fieldNames, fieldIndexByName);

  for (let frameIndex = 0; frameIndex < data.length; frameIndex++) {
    const frame = data[frameIndex];
    if (!frame) continue; // skip if frame is undefined
    for (let valueIndex = 0; valueIndex < frame.length; valueIndex++) {
      const key = keyFactory(frameIndex, valueIndex);
      const value = valueMapper(frameIndex, valueIndex);

      if (!Array.isArray(valuesByKey[key])) {
        valuesByKey[key] = [value];
        valuesInOrder.push(createPointer(key, valuesByKey));
        continue;
      }

      let valueWasMerged = false;

      valuesByKey[key] =
        valuesByKey[key]?.map((existing) => {
          if (!isMergable(existing, value)) {
            return existing;
          }
          valueWasMerged = true;
          return { ...existing, ...value };
        }) ?? [];

      if (!valueWasMerged) {
        valuesByKey[key]?.push(value);
        valuesInOrder.push(createPointer(key, valuesByKey));
      }
    }
  }

  for (const pointer of valuesInOrder) {
    const v = valuesByKey[pointer.key];
    if (v) {
      const value = v[pointer.index];

      if (value) {
        dataFrame.add(value);
      }
    }
  }

  return [dataFrame as DataFrame];
};

const copyFieldStructure = (field: Field): Field => {
  // Cast to Field due to lodash omit's types saying it returns a Partial<Field> (which makes all properties
  // possibly undefined)
  return {
    ...omit(field, ['values']),
    values: [],
  } as Field;
};

const createKeyFactory = (
  data: DataFrame[],
  fieldPointerByName: Record<string, Record<string, number>>,
  keyFieldNames: string[]
) => {
  const factoryIndex = keyFieldNames.reduce((index: Record<string, number[]>, fieldName) => {
    const obj = fieldPointerByName[fieldName] ?? {};
    return Object.keys(obj).reduce((index: Record<string, number[]>, frameIndex) => {
      index[frameIndex] = index[frameIndex] || [];
      const arr = index[frameIndex] ?? [];
      const frameIndexToFieldIndexMap = fieldPointerByName[fieldName];
      if (frameIndexToFieldIndexMap) {
        const fieldIndex = frameIndexToFieldIndexMap[frameIndex];
        if (fieldIndex) {
          arr.push(fieldIndex);
        }
      }
      return index;
    }, index);
  }, {});

  return (frameIndex: number, valueIndex: number): string => {
    const obj = factoryIndex[frameIndex] ?? [];
    return obj.reduce((key: string, fieldIndex: number) => {
      const d = data[frameIndex]?.fields[fieldIndex];
      if (d) {
        return key + d.values[valueIndex];
      }
      return '';
    }, '');
  };
};

const createValueMapper = (
  data: DataFrame[],
  fieldByName: Set<string>,
  fieldIndexByName: Record<string, Record<number, number>>
) => {
  return (frameIndex: number, valueIndex: number) => {
    const value: Record<string, unknown> = {};
    const fieldNames = Array.from(fieldByName);

    for (const fieldName of fieldNames) {
      const fieldIndexByFrameIndex = fieldIndexByName[fieldName];
      if (!fieldIndexByFrameIndex) {
        continue;
      }

      const fieldIndex = fieldIndexByFrameIndex[frameIndex];
      if (typeof fieldIndex !== 'number') {
        continue;
      }

      const frame = data[frameIndex];
      if (!frame || !frame.fields) {
        continue;
      }

      const field = frame.fields[fieldIndex];
      if (!field || !field.values) {
        continue;
      }

      value[fieldName] = field.values[valueIndex];
    }

    return value;
  };
};

const isMergable = (existing: Record<string, unknown>, value: Record<string, unknown>): boolean => {
  let mergable = true;

  for (const prop in value) {
    if (typeof existing[prop] === 'undefined') {
      continue;
    }

    if (existing[prop] === null) {
      continue;
    }

    if (existing[prop] !== value[prop]) {
      mergable = false;
      break;
    }
  }

  return mergable;
};

const fieldExistsInAllFrames = (
  fieldIndexByName: Record<string, Record<number, number>>,
  field: Field,
  data: DataFrame[]
) => {
  const f = fieldIndexByName[field.name];
  if (f) {
    return Object.keys(f).length === data.length;
  }
  return [];
};

const createPointer = (key: string, valuesByKey: Record<string, Array<Record<string, unknown>>>): ValuePointer => {
  const v = valuesByKey[key];
  return {
    key,
    index: v?.length ? v.length - 1 : 0,
  };
};
